import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { catalogApi, type ServiceCatalog, type CatalogServiceEntry, type CatalogPath, type CatalogCategory } from '@/api/catalogApi';

interface CatalogContextValue {
  catalog: ServiceCatalog | null;
  loading: boolean;
  /** Resolve a service slug to its catalog entry. */
  serviceBySlug: (slug: string) => CatalogServiceEntry | undefined;
  /** Resolve a path id ("A" | "B") to its definition. */
  pathById: (id: string) => CatalogPath | undefined;
  /** Resolve a category within a path. */
  categoryById: (pathId: string, categoryId: string) => CatalogCategory | undefined;
}

const CatalogContext = createContext<CatalogContextValue>({
  catalog: null,
  loading: true,
  serviceBySlug: () => undefined,
  pathById: () => undefined,
  categoryById: () => undefined,
});

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [catalog, setCatalog] = useState<ServiceCatalog | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    catalogApi
      .get()
      .then((res) => {
        if (active && res.data?.catalog) setCatalog(res.data.catalog);
      })
      .catch(() => {
        // Browse pages handle a null catalog with a graceful loading/empty state.
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const value = useMemo<CatalogContextValue>(() => {
    const bySlug = new Map<string, CatalogServiceEntry>(
      (catalog?.services ?? []).map((s) => [s.slug, s]),
    );
    const pathMap = new Map<string, CatalogPath>(
      (catalog?.paths ?? []).map((p) => [p.id, p]),
    );
    return {
      catalog,
      loading,
      serviceBySlug: (slug) => bySlug.get(slug),
      pathById: (id) => pathMap.get(id),
      categoryById: (pathId, categoryId) =>
        pathMap.get(pathId)?.categories.find((c) => c.id === categoryId),
    };
  }, [catalog, loading]);

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
  return useContext(CatalogContext);
}
